import React from 'react'
import { Flex, useDisclosure } from '@chakra-ui/react'
import { Trans, t } from '@lingui/macro'
import Loader from '../../../../../components/common/loader/Loader'
import {
  QuizAggregateResultsDocument,
  useQuizAggregateResultsQuery
} from './QuizAggregateResults.codegen'
import { TopicElementTypeEnum } from 'types/TopicElementTypeEnum'
import { FreeQuizAnswersBreakdown } from '../freeQuizAnswersBreakdown/FreeQuizAnswersBreakdown'
import {
  percentageOfTotalFormatter,
  QuizOption
} from '../quizOption/QuizOption'
import { QuizElementTypeShort } from '../../../constants/QuizElementTypes'
import { ButtonLight } from 'front-end/src/components'
import '../quizResults/QuizResults.less'
import { DownloadCSVButton } from 'front-end/src/components/common/download-csv-button/DownloadCSVButton'
import { stripHTMLTags } from 'shared/HTML'

export function QuizAggregateResults(props: {
  topicId: number
  interval: {
    from: string
    to: string
  }
  groupIds: number[] | null
}) {
  const { data, loading } = useQuizAggregateResultsQuery({
    variables: props
  })
  if (loading) {
    return <Loader />
  }
  const {
    topic: { lastTopicVersion }
  } = data

  if (!lastTopicVersion || !lastTopicVersion.publishedQuizzes.length) {
    return (
      <div className="workbook-quizzes">
        <p className="AnalyticsPage__no-data">
          <Trans>No data available</Trans>
        </p>
      </div>
    )
  }

  return (
    <div className="workbook-quizzes">
      {lastTopicVersion.publishedQuizzes.map((publishedQuiz, i) => {
        const isOpenQuiz = publishedQuiz.type === TopicElementTypeEnum.QUIZ_OPEN
        const isFreeQuiz = publishedQuiz.type === TopicElementTypeEnum.QUIZ_FREE

        const { responsesBreakdown, options } = publishedQuiz
        const totalResponses =
          responsesBreakdown.correct + responsesBreakdown.incorrect

        return (
          <div className="QuizResult" key={`QuizResult-${i}`}>
            <div className="QuizResult__content">
              <div className="QuizResult__header">
                <Trans>Question {i + 1} - type</Trans>{' '}
                {QuizElementTypeShort[publishedQuiz.type]}
              </div>
              <div
                className="page-content  page-content--analytics"
                dangerouslySetInnerHTML={{
                  __html: ((publishedQuiz as any).content ||
                    publishedQuiz.data?.content) as string // TODO validate that we indeed have elements that only have `content` and no `data.content`
                }}
              />
              <div className="QuizResult__answer">
                {isFreeQuiz ? (
                  <FreeQuizShowSwitch elementId={publishedQuiz.id} {...props} />
                ) : (
                  options &&
                  options.map((option) => (
                    <QuizOption
                      key={option.id}
                      responsesBreakdown={responsesBreakdown}
                      option={option}
                      isCorrect={publishedQuiz.response_is_correct}
                      isOpenQuiz={isOpenQuiz}
                      totalResponses={totalResponses}
                      viewAsStudent={false}
                    />
                  ))
                )}
              </div>
            </div>

            <div className="QuizResult__assessment">
              {!isFreeQuiz && (
                <>
                  <div className="QuizResult__total">
                    {percentageOfTotalFormatter(
                      responsesBreakdown.correct,
                      totalResponses
                    )}
                  </div>

                  {!isOpenQuiz && (
                    <span className="small">
                      {totalResponses === 0
                        ? t`Nobody answered this question`
                        : t`${responsesBreakdown.correct} of ${totalResponses} answered correctly`}
                    </span>
                  )}
                </>
              )}
            </div>
          </div>
        )
      })}

      <Flex justify="end" p="30px">
        <DownloadCSVButton
          fileName={`${props.topicId}-quiz-results`}
          dataPath={'data.topic.lastTopicVersion.publishedQuizzes'}
          queryOptions={() => ({
            variables: props,
            query: QuizAggregateResultsDocument
          })}
          prepareData={(data) => {
            const res: any[] = []
            data.forEach((question, i) => {
              res.push({
                A: '',
                B: '',
                C: '',
                D: ''
              })

              if (question.type === 'QUIZ_FREE') {
                res.push({
                  A: `Q ${i + 1}. ${stripHTMLTags(question.data.content)}`,
                  B: `Answer`,
                  C: `User name`
                })
                question.freeTextResponses.forEach((response) => {
                  res.push({
                    A: '',
                    B: response.freeQuizResponse,
                    C: response.enrolment.user.display_name,
                    D: ''
                  })
                })
              } else {
                res.push({
                  A: `Q ${i + 1}. ${stripHTMLTags(question.data.content)}`
                })
                const totalResponses =
                  question.responsesBreakdown.correct +
                  question.responsesBreakdown.incorrect
                res.push(
                  { A: '' },
                  {
                    A: 'Answered Options',
                    B: 'Response Count',
                    C: 'Response Percent',
                    D: 'Response'
                  }
                )

                question.options.forEach((option) => {
                  res.push({
                    A: stripHTMLTags(option.content),
                    B: option.clickRate.clicks,
                    C:
                      totalResponses > 0
                        ? `${(
                            (option.clickRate.clicks / totalResponses) *
                            100
                          ).toFixed()}%`
                        : '-',
                    D: option.response_is_correct ? 'Correct' : 'Incorrect'
                  })
                })
              }
            })

            return res
          }}
        />
      </Flex>
    </div>
  )
}

const FreeQuizShowSwitch = (props: {
  topicId: number
  interval: {
    from: string
    to: string
  }
  groupIds: number[] | null
  elementId: number
}) => {
  const { isOpen, onOpen, onClose } = useDisclosure()
  if (!isOpen) {
    return (
      <ButtonLight
        size="sm"
        onClick={() => {
          onOpen()
        }}
      >
        <Trans>Show answers</Trans>
      </ButtonLight>
    )
  }

  return (
    <>
      <ButtonLight
        size="sm"
        onClick={() => {
          onClose()
        }}
      >
        <Trans>Hide answers</Trans>
      </ButtonLight>{' '}
      <FreeQuizAnswersBreakdown {...props} />{' '}
      <ButtonLight
        size="sm"
        onClick={() => {
          onClose()
        }}
      >
        <Trans>Hide answers</Trans>
      </ButtonLight>
    </>
  )
}
